import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { usePlayer } from "../context/PlayerContext";
import { showUpgradeExternally } from "../components/UpgradePrompt";
import { askForPremium } from "../lib/askPremium";
import AboutTv from "../components/AboutTv";
import { requestLightsPanel } from "../lib/lightsBus";

// The About page: a little steel TV with the words on its screen (components/AboutTv.js).
//  - Tap the screen: a Premium member's (or admin's) Favorites play inside the screen. A guest hears the demo song (the admin picks it in
//    "Edit text"; it starts as "A.I. Killed The Video Star") and gets the "Create a free login" box when it ends or when they tap the screen
//    again to switch it off. A free member gets the same box the Favorites page gives them.
//  - The light bulb under the TV opens the player's Lights panel for the song that is playing (members); a guest gets the login box.
//  - "Read the text" under the TV grows the words to fill the window.
//  - An admin sees "Edit text": the heading and paragraphs are saved on the server (backend/about_text.py) and everyone sees them at once.
// The wording below is the original. The same words are also written into frontend/public/index.html, so programs that do not run
// JavaScript read them; a saved edit changes this page but not that file. If you change the original here, change index.html too.
export const ABOUT_DEFAULT = {
  title: "About Dan The Man TV",
  body: [
    "Dan The Man TV is a music and music video channel from Austin, Texas, and the Texas Hill Country. Every song and video here is original, written and produced by Dan The Man with A.I. assistance.",
    "There are no ads. Most songs play in full for everyone. Some songs are Premium: anyone can watch a free preview, and Premium members get the full song. A free login lets you save a playlist and pick your own colors for the lights that glow with each song.",
    "Many songs have lyrics and closed captions (the CC button), so you can sing along. A few songs have explicit language. Those need a free 18+ check first.",
    "Follow Dan The Man TV on X: [@DanTheMan_TV](https://x.com/DanTheMan_TV). Questions or ideas? Use Contact or Send Dan a suggestion at the bottom of any page.",
  ].join("\n\n"),
};

// Words with a link are written [words to show](https://address). A bare web address also becomes a link. Only http and https are used.
const LINK = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<>()]*[^\s<>().,;:!?'"])/g;
export function renderInline(text) {
  const out = [];
  let last = 0, m, n = 0;
  LINK.lastIndex = 0;
  while ((m = LINK.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const href = m[2] || m[3], label = m[1] || m[3];
    out.push(<a key={`l${n++}`} href={href} target="_blank" rel="noopener noreferrer">{label}</a>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function Editor({ initial, demoId, onClose, onSaved }) {
  const [title, setTitle] = useState(initial.title);
  const [body, setBody] = useState(initial.body);
  const [songs, setSongs] = useState(null);                  // every song that can be the demo song (null while loading)
  const [demo, setDemo] = useState(demoId || "");             // the chosen demo song ("" = none)
  const [demoWas, setDemoWas] = useState(demoId || "");
  useEffect(() => {
    let alive = true;
    api.get("/admin/about/demo").then((r) => {
      if (!alive) return;
      setSongs(r.data.songs || []);
      setDemo(r.data.video_id || ""); setDemoWas(r.data.video_id || "");
    }).catch(() => { if (alive) setSongs([]); });
    return () => { alive = false; };
  }, []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [askReset, setAskReset] = useState(false);
  const titleRef = useRef(null);
  useEffect(() => { if (titleRef.current) titleRef.current.focus(); }, []);
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [busy, onClose]);
  const fail = (e) => setErr((e && e.response && e.response.data && e.response.data.detail) || "That did not save. Try again in a moment.");
  const save = async () => {
    setBusy(true); setErr("");
    try {
      if (songs && demo !== demoWas) { await api.put("/admin/about/demo", { video_id: demo || null }); setDemoWas(demo); }
      const { data } = await api.put("/admin/about", { title, body });
      onSaved(data, demo || null);
    } catch (e) { fail(e); setBusy(false); }
  };
  const reset = async () => {
    if (!askReset) { setAskReset(true); return; }
    setBusy(true); setErr("");
    try { await api.delete("/admin/about"); onSaved(null, demoWas || null); } catch (e) { fail(e); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/75" role="dialog" aria-modal="true" aria-label="Edit the About text" data-testid="about-editor">
      <div className="w-full max-w-2xl max-h-full overflow-y-auto rounded-2xl border border-cyan-400/40 bg-zinc-950 p-5 sm:p-6 shadow-2xl">
        <h2 className="font-display text-2xl font-extrabold mb-4">Edit the About text</h2>
        <label className="block text-sm text-zinc-300 mb-1" htmlFor="about-edit-title">Heading</label>
        <input id="about-edit-title" ref={titleRef} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120}
          className="w-full rounded-lg bg-zinc-900 border border-white/15 px-3 py-2 text-white mb-4" data-testid="about-edit-title" />
        <label className="block text-sm text-zinc-300 mb-1" htmlFor="about-edit-body">Paragraphs</label>
        <textarea id="about-edit-body" value={body} onChange={(e) => setBody(e.target.value)} rows={14}
          className="w-full rounded-lg bg-zinc-900 border border-white/15 px-3 py-2 text-white leading-relaxed" data-testid="about-edit-body" />
        <p className="text-xs text-zinc-400 mt-2">
          Leave a blank line between paragraphs. To make a link, write [the words to show](https://the-address). A plain web address also becomes a link.
        </p>
        <label className="block text-sm text-zinc-300 mt-5 mb-1" htmlFor="about-edit-demo">Song the TV plays for visitors without a login</label>
        <select id="about-edit-demo" value={demo} onChange={(e) => setDemo(e.target.value)} disabled={songs === null || busy}
          className="w-full rounded-lg bg-zinc-900 border border-white/15 px-3 py-2 text-white" data-testid="about-edit-demo">
          <option value="">No demo song (they get the "Create a free login" box)</option>
          {(songs || []).map((v) => <option key={v.id} value={v.id}>{v.title}{v.artist ? ` \u2013 ${v.artist}` : ""}{v.premium ? " (Premium: they hear only the preview)" : ""}</option>)}
        </select>
        <p className="text-xs text-zinc-400 mt-2">
          A visitor taps the TV and this song plays on its screen with its lights. The "Create a free login" box comes up when it ends or when they switch it off. It saves with the words.
        </p>
        {err && <p className="text-sm text-red-400 mt-3" role="alert" data-testid="about-edit-error">{err}</p>}
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button type="button" onClick={save} disabled={busy} className="px-5 py-2.5 rounded-full bg-cyan-400 text-black font-semibold hover:bg-cyan-300 disabled:opacity-50" data-testid="about-edit-save">Save</button>
          <button type="button" onClick={onClose} disabled={busy} className="px-5 py-2.5 rounded-full border border-white/20 text-zinc-200 hover:bg-white/10 disabled:opacity-50" data-testid="about-edit-cancel">Cancel</button>
          <button type="button" onClick={reset} disabled={busy} className="ml-auto text-sm text-zinc-400 hover:text-white underline underline-offset-4 disabled:opacity-50" data-testid="about-edit-reset">
            {askReset ? "Press again to put back the original words" : "Put back the original words"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function About() {
  const { user } = useAuth();
  const player = usePlayer();
  const [saved, setSaved] = useState(null);       // the words an admin saved, or null for the original
  const [ready, setReady] = useState(false);      // the screen shows its words once we know which ones
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState("");
  const noteTimer = useRef(0);
  const favRef = useRef(null);                    // the member's Favorites, fetched ahead so a tap can start a song at once
  const [demoId, setDemoId] = useState(null);     // the demo song a guest hears (from the server)
  const demoRef = useRef(null);                   // that song, fetched ahead for a guest
  const isAdmin = !!user && user.role === "admin";

  // A page opened from the footer should start at the top.
  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    let alive = true;
    const t = setTimeout(() => { if (alive) setReady(true); }, 1500);       // never leave the screen blank if the server is slow
    api.get("/about").then((r) => {
      if (!alive || !r.data) return;
      if (r.data.title && r.data.body) setSaved({ title: r.data.title, body: r.data.body });
      setDemoId(r.data.demo_id || null);
    })
      .catch(() => {}).finally(() => { if (alive) { clearTimeout(t); setReady(true); } });
    return () => { alive = false; clearTimeout(t); };
  }, []);
  useEffect(() => () => clearTimeout(noteTimer.current), []);

  const canUsePremium = !!(player && player.canUsePremium);
  const userId = user ? user.id : null;
  useEffect(() => {
    favRef.current = null;
    if (!userId || !canUsePremium) return undefined;
    let alive = true;
    api.get("/favorites").then((r) => { if (alive) favRef.current = r.data; }).catch(() => {});
    return () => { alive = false; };
  }, [userId, canUsePremium]);

  useEffect(() => {
    demoRef.current = null;
    if (userId || !demoId) return undefined;       // only a guest hears the demo song
    let alive = true;
    api.get(`/videos/${demoId}`).then((r) => { if (alive) demoRef.current = r.data; }).catch(() => {});
    return () => { alive = false; };
  }, [userId, demoId]);

  const flash = useCallback((msg) => {
    setNote(msg);
    clearTimeout(noteTimer.current);
    noteTimer.current = setTimeout(() => setNote(""), 6000);
  }, []);

  const onScreen = !!(player && player.inlineTv && player.current && !player.expanded);
  const tapScreen = useCallback(async () => {
    if (!player) return;
    if (!user) {
      const loginBox = () => showUpgradeExternally("Create a free login", {
        ctaLabel: "Create a free login", ctaPath: "/register",
        body: "Sign up to keep watching full tracks, build playlists, and save favorites.",
      });
      // a guest: the demo song plays on the screen; a second tap switches it off and brings up the sign-up box (the box also comes up when the song ends)
      if (onScreen) { if (player.playing) player.setPlaying(false); loginBox(); return; }
      if (!demoId) { loginBox(); return; }
      let song = demoRef.current;
      if (!song) {
        try { const r = await api.get(`/videos/${demoId}`); song = r.data; demoRef.current = song; } catch { loginBox(); return; }
      }
      if (!song || song.age_locked) { loginBox(); return; }
      player.playQueue([song], 0, { inline: true });
      return;
    }
    if (!canUsePremium) { askForPremium("Favorites are a premium feature"); return; }
    if (onScreen) { player.toggle(); return; }             // a song is already on the screen: the tap pauses or plays it
    let list = favRef.current;
    if (!list) {
      try { const r = await api.get("/favorites"); list = r.data; favRef.current = list; } catch { flash("Could not load your favorites. Try again in a moment."); return; }
    }
    if (!list.length) { flash("No favorites yet. Tap the heart on any song to save it here."); return; }
    const first = list.findIndex((v) => !v.age_locked);
    player.playQueue(list, first >= 0 ? first : 0, { inline: true, playlist: true });   // Favorites on the TV play through like a playlist
    api.get("/favorites").then((r) => { favRef.current = r.data; }).catch(() => {});     // a fresh list for next time
  }, [player, user, canUsePremium, onScreen, demoId, flash]);

  // The light bulb: a member changes the lights of the song that is playing (the player's Lights panel opens over the page);
  // a guest gets the same "Create a free login" box as everywhere else.
  const bulb = useCallback(() => {
    if (!user) {
      showUpgradeExternally("Create a free login", {
        ctaLabel: "Create a free login", ctaPath: "/register",
        body: "Sign up to pick your own colors and effects for the lights.",
      });
      return;
    }
    if (!requestLightsPanel()) flash("Play a song first, then the light bulb changes that song's lights. Or pick a look in the TV lights row.");
  }, [user, flash]);

  const words = saved || ABOUT_DEFAULT;
  const paragraphs = words.body.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  return (
    <div data-testid="about-page">
      <AboutTv onScreenTap={tapScreen} onScreen={onScreen} note={note} ready={ready} onLights={bulb} onEdit={isAdmin ? () => setEditing(true) : undefined}>
        <h1>{words.title}</h1>
        {paragraphs.map((p, i) => <p key={i} style={{ whiteSpace: "pre-line" }}>{renderInline(p)}</p>)}
      </AboutTv>
      {editing && <Editor initial={words} demoId={demoId} onClose={() => setEditing(false)} onSaved={(w, d) => { setSaved(w && w.title ? { title: w.title, body: w.body } : null); setDemoId(d || null); setEditing(false); }} />}
    </div>
  );
}
